import { router, useForm } from '@inertiajs/react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import SurveyLayout from '@/layouts/SurveyLayout'
import RichTextEditor, { VariableMenu } from '@/components/RichTextEditor'
import type { SurveyDetail, RespondentInviteRow, RespondentCounts } from '@/types'
import { useAccountPath } from '@/lib/useAccountPath'

interface RespondentsProps {
  survey: SurveyDetail
  respondents: RespondentInviteRow[]
  counts: RespondentCounts
  invitation_subject: string
  invitation_body: string
  reminder_subject: string
  reminder_body: string
}

interface PreviewRow {
  first_name: string
  last_name: string
  email_address: string
  valid: boolean
  errors: string[]
  already_respondent: boolean
  duplicate_in_file: boolean
}

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

function formatDate(iso: string | null): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

/** Who an invitation can go to: any incomplete, non-erased respondent.
 *  Invitations may be re-sent (bounce corrections etc.); the UI warns before
 *  re-inviting someone already invited. Never a completed respondent. */
function invitable(r: RespondentInviteRow): boolean {
  return !r.completed_at && !r.erased
}

/** Who a reminder can go to: invited, not completed, not erased. */
function remindable(r: RespondentInviteRow): boolean {
  return !!r.invitation_sent_at && !r.completed_at && !r.erased
}

export default function Respondents({ survey, respondents, counts, ...templates }: RespondentsProps) {
  const ap = useAccountPath()
  const basePath = ap(`/surveys/${survey.id}/respondents`)
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [showAdd, setShowAdd] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const importDialog = useRef<HTMLDialogElement>(null)
  const sendDialog = useRef<HTMLDialogElement>(null)
  const historyDialog = useRef<HTMLDialogElement>(null)
  const editDialog = useRef<HTMLDialogElement>(null)
  const [sendKind, setSendKind] = useState<'invitation' | 'reminder'>('invitation')
  const [historyRow, setHistoryRow] = useState<RespondentInviteRow | null>(null)
  const [editRow, setEditRow] = useState<RespondentInviteRow | null>(null)

  function openHistory(row: RespondentInviteRow) {
    setHistoryRow(row)
    historyDialog.current?.showModal()
  }

  function openEdit(row: RespondentInviteRow) {
    setEditRow(row)
    editDialog.current?.showModal()
  }

  const selectable = respondents.filter((r) => !r.erased)
  const allSelected = selectable.length > 0 && selectable.every((r) => selected.has(r.id))

  function toggleAll() {
    setSelected(allSelected ? new Set() : new Set(selectable.map((r) => r.id)))
  }

  function toggle(id: number) {
    setSelected((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Audience for a send: the eligible subset of the explicit selection when
  // there is one, otherwise everyone eligible. `invitable`/`remindable`
  // already encode server-side eligibility, so the modal count matches what
  // the server will queue. The server re-scopes regardless.
  function audienceFor(kind: 'invitation' | 'reminder'): RespondentInviteRow[] {
    const eligible = respondents.filter(kind === 'invitation' ? invitable : remindable)
    if (selected.size === 0) return eligible
    return eligible.filter((r) => selected.has(r.id))
  }

  function openSend(kind: 'invitation' | 'reminder') {
    setSendKind(kind)
    sendDialog.current?.showModal()
  }

  function handleDelete(row: RespondentInviteRow) {
    if (!confirm(`Remove ${row.first_name || row.email_address} from this survey? Their invitation link stops working.`)) return
    router.delete(`${basePath}/${row.id}`, { preserveScroll: true })
  }

  async function copyLink(row: RespondentInviteRow) {
    await navigator.clipboard.writeText(row.survey_url)
    setNotice(`Link for ${row.first_name || row.email_address} copied.`)
    setTimeout(() => setNotice(null), 2500)
  }

  return (
    <SurveyLayout survey={survey}>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Headline */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-2xl font-bold">Respondents</h2>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={respondents.filter(remindable).length === 0}
              onClick={() => openSend('reminder')}
            >
              <span className="iconify lucide--bell-ring size-4" />
              Send reminders
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              disabled={respondents.filter(invitable).length === 0}
              onClick={() => openSend('invitation')}
            >
              <span className="iconify lucide--send size-4" />
              Send invitations
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => importDialog.current?.showModal()}>
              <span className="iconify lucide--upload size-4" />
              Import
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}>
              <span className="iconify lucide--user-plus size-4" />
              Add Respondent
            </button>
          </div>
        </div>

        {notice && (
          <div role="alert" className="alert alert-soft alert-success text-sm">
            <span className="iconify lucide--check size-4" />
            <span>{notice}</span>
          </div>
        )}

        {survey.status !== 'active' && (
          <div role="alert" className="alert alert-soft alert-info text-sm">
            <span className="iconify lucide--info size-4" />
            <span>
              You can build your respondent list now, but invitations can only be sent while the survey is active
              {survey.status === 'draft' ? ' — publish it first.' : '.'}
            </span>
          </div>
        )}

        {/* Counts */}
        <div className="stats stats-horizontal bg-base-100 w-full">
          <div className="stat py-3">
            <div className="stat-title text-xs">Respondents</div>
            <div className="stat-value text-2xl">{counts.total}</div>
          </div>
          <div className="stat py-3">
            <div className="stat-title text-xs">Not yet invited</div>
            <div className="stat-value text-2xl">{counts.uninvited}</div>
          </div>
          <div className="stat py-3">
            <div className="stat-title text-xs">Awaiting response</div>
            <div className="stat-value text-2xl">{counts.awaiting}</div>
          </div>
          <div className="stat py-3">
            <div className="stat-title text-xs">Completed</div>
            <div className="stat-value text-2xl text-success">{counts.completed}</div>
          </div>
        </div>

        {showAdd && <AddRespondentForm basePath={basePath} onClose={() => setShowAdd(false)} />}

        {/* Respondent table */}
        <div className="card bg-base-100">
          <div className="card-body">
            {respondents.length === 0 ? (
              <p className="text-base-content/50 text-sm py-6 text-center">
                No respondents yet. Add people one at a time or import a CSV — then send everyone their personal
                invitation link.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th className="w-8">
                        <input type="checkbox" className="checkbox checkbox-sm" checked={allSelected} onChange={toggleAll} />
                      </th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {respondents.map((row) => (
                      <tr key={row.id} className="hover:bg-base-200/40">
                        <td>
                          {!row.erased && (
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm"
                              checked={selected.has(row.id)}
                              onChange={() => toggle(row.id)}
                            />
                          )}
                        </td>
                        <td className={row.erased ? 'italic text-base-content/40' : ''}>
                          {row.erased ? 'Erased' : `${row.first_name} ${row.last_name}`.trim()}
                        </td>
                        <td className={row.erased ? 'text-base-content/40' : ''}>{row.erased ? '—' : row.email_address}</td>
                        <td>
                          {row.completed_at ? (
                            <span className="badge badge-success badge-sm">Completed {formatDate(row.completed_at)}</span>
                          ) : row.invitation_sent_at ? (
                            <span className="flex flex-wrap gap-1">
                              <span className="badge badge-info badge-sm">Invited {formatDate(row.invitation_sent_at)}</span>
                              {row.reminder_sent_at && (
                                <span className="badge badge-soft badge-sm">Reminded {formatDate(row.reminder_sent_at)}</span>
                              )}
                            </span>
                          ) : (
                            <span className="text-base-content/40 text-sm">Not invited</span>
                          )}
                        </td>
                        <td className="text-end">
                          {row.email_history.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-square btn-ghost btn-sm"
                              title="Email history"
                              onClick={() => openHistory(row)}
                            >
                              <span className="iconify lucide--history size-4" />
                            </button>
                          )}
                          {!row.erased && (
                            <button
                              type="button"
                              className="btn btn-square btn-ghost btn-sm"
                              title="Edit respondent"
                              onClick={() => openEdit(row)}
                            >
                              <span className="iconify lucide--pencil size-4" />
                            </button>
                          )}
                          {!row.erased && (
                            <button
                              type="button"
                              className="btn btn-square btn-ghost btn-sm"
                              title="Copy personal survey link"
                              onClick={() => void copyLink(row)}
                            >
                              <span className="iconify lucide--link size-4" />
                            </button>
                          )}
                          {/* An incomplete row can always be removed — including
                              an erased one, so it doesn't linger unremovable. */}
                          {!row.completed_at && (
                            <button
                              type="button"
                              className="btn btn-square btn-ghost btn-sm text-error"
                              title="Remove from survey"
                              onClick={() => handleDelete(row)}
                            >
                              <span className="iconify lucide--trash-2 size-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <HistoryModal dialogRef={historyDialog} row={historyRow} />
      <EditModal dialogRef={editDialog} row={editRow} basePath={basePath} onClosed={() => setEditRow(null)} />
      <ImportModal dialogRef={importDialog} basePath={basePath} />
      <SendModal
        dialogRef={sendDialog}
        kind={sendKind}
        basePath={basePath}
        audience={audienceFor(sendKind)}
        surveyActive={survey.status === 'active'}
        initialSubject={sendKind === 'invitation' ? templates.invitation_subject : templates.reminder_subject}
        initialBody={sendKind === 'invitation' ? templates.invitation_body : templates.reminder_body}
      />
    </SurveyLayout>
  )
}

/* ----- Add one respondent ----- */

function AddRespondentForm({ basePath, onClose }: { basePath: string; onClose: () => void }) {
  const { data, setData, post, processing } = useForm({ first_name: '', last_name: '', email_address: '' })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post(`${basePath}?respondent_form=1`, {
      preserveScroll: true,
      onSuccess: () => onClose(),
    })
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1.5fr_auto] gap-3 items-end">
        <fieldset className="fieldset">
          <legend className="fieldset-legend">First name</legend>
          <input type="text" className="input input-sm w-full" value={data.first_name} onChange={(e) => setData('first_name', e.target.value)} required />
        </fieldset>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Last name</legend>
          <input type="text" className="input input-sm w-full" value={data.last_name} onChange={(e) => setData('last_name', e.target.value)} />
        </fieldset>
        <fieldset className="fieldset">
          <legend className="fieldset-legend">Email</legend>
          <input type="email" className="input input-sm w-full" value={data.email_address} onChange={(e) => setData('email_address', e.target.value)} required />
        </fieldset>
        <div className="flex gap-2 pb-1">
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={processing}>
            {processing && <span className="loading loading-spinner loading-sm" />}
            Add
          </button>
        </div>
      </div>
    </form>
  )
}

/* ----- Per-recipient email history ----- */

function HistoryModal({ dialogRef, row }: { dialogRef: React.RefObject<HTMLDialogElement | null>; row: RespondentInviteRow | null }) {
  const name = row ? `${row.first_name} ${row.last_name}`.trim() || row.email_address : ''

  return (
    <dialog ref={dialogRef} className="modal">
      <div className="modal-box max-w-lg">
        <h3 className="text-lg font-bold">Email history</h3>
        {row && <p className="text-sm text-base-content/60">{name}</p>}

        <div className="py-4 space-y-2">
          {!row || row.email_history.length === 0 ? (
            <p className="text-sm text-base-content/50">No emails sent to this respondent yet.</p>
          ) : (
            row.email_history.map((log) => (
              <div key={log.id} className="flex items-start gap-3 py-2 border-b border-base-200 last:border-0">
                <span className={`badge badge-sm shrink-0 ${log.email_type === 'invitation' ? 'badge-info' : 'badge-soft'}`}>
                  {log.email_type === 'invitation' ? 'Invitation' : 'Reminder'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm truncate">{log.subject}</p>
                  <p className="text-xs text-base-content/50">
                    {new Date(log.sent_at).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="modal-action">
          <form method="dialog"><button className="btn btn-ghost btn-sm">Close</button></form>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop"><button>close</button></form>
    </dialog>
  )
}

/* ----- Edit a respondent (correct a bounced email / typo) ----- */

function EditModal({ dialogRef, row, basePath, onClosed }: { dialogRef: React.RefObject<HTMLDialogElement | null>; row: RespondentInviteRow | null; basePath: string; onClosed: () => void }) {
  const { data, setData, patch, processing, errors } = useForm({ first_name: '', last_name: '', email_address: '' })
  // The modal stays mounted; sync the form whenever a row is opened. The parent
  // clears its editRow to null on close, so reopening the same row is a real
  // dependency change and always re-syncs (no stale values).
  useEffect(() => {
    if (row) setData({ first_name: row.first_name, last_name: row.last_name, email_address: row.email_address })
  }, [row]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!row) return
    patch(`${basePath}/${row.id}?respondent_form=1`, {
      preserveScroll: true,
      onSuccess: () => dialogRef.current?.close(),
    })
  }

  return (
    <dialog ref={dialogRef} className="modal" onClose={onClosed}>
      <div className="modal-box max-w-md">
        <h3 className="text-lg font-bold">Edit respondent</h3>
        <p className="text-sm text-base-content/60">Corrections apply everywhere this person appears in your account.</p>
        <form onSubmit={handleSubmit} className="py-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">First name</legend>
              <input type="text" className="input input-sm w-full" value={data.first_name} onChange={(e) => setData('first_name', e.target.value)} required />
            </fieldset>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Last name</legend>
              <input type="text" className="input input-sm w-full" value={data.last_name} onChange={(e) => setData('last_name', e.target.value)} />
            </fieldset>
          </div>
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Email</legend>
            <input type="email" className="input input-sm w-full" value={data.email_address} onChange={(e) => setData('email_address', e.target.value)} required />
            {errors.email_address && <p className="text-xs text-error mt-1">{errors.email_address}</p>}
          </fieldset>
          <div className="modal-action">
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => dialogRef.current?.close()}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={processing}>
              {processing && <span className="loading loading-spinner loading-sm" />}
              Save
            </button>
          </div>
        </form>
      </div>
      <form method="dialog" className="modal-backdrop"><button>close</button></form>
    </dialog>
  )
}

/* ----- CSV import: upload -> preview -> commit ----- */

function ImportModal({ dialogRef, basePath }: { dialogRef: React.RefObject<HTMLDialogElement | null>; basePath: string }) {
  const [rows, setRows] = useState<PreviewRow[] | null>(null)
  const [checked, setChecked] = useState<boolean[]>([])
  const [fileErrors, setFileErrors] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ created: number; failed: { email_address: string; errors: string[] }[] } | null>(null)
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const fileInput = useRef<HTMLInputElement>(null)

  function reset() {
    setRows(null)
    setChecked([])
    setFileErrors([])
    setResult(null)
    setFile(null)
    setDragging(false)
    if (fileInput.current) fileInput.current.value = ''
  }

  function acceptFile(f: File | undefined | null) {
    if (!f) return
    const ok = /\.(xlsx|csv)$/i.test(f.name)
    if (!ok) {
      setFileErrors(['Please choose an Excel (.xlsx) or CSV file.'])
      return
    }
    setFileErrors([])
    setFile(f)
  }

  async function handlePreview() {
    if (!file) return
    setBusy(true)
    setFileErrors([])
    const form = new FormData()
    form.append('file', file)
    const response = await fetch(`${basePath}/import_preview`, {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
      body: form,
    })
    setBusy(false)
    if (!response.ok) {
      setFileErrors(['Could not read that file. Download the template and make sure it has First Name, Last Name, and Email columns.'])
      return
    }
    const body = (await response.json()) as { rows: PreviewRow[]; errors: string[] }
    setFileErrors(body.errors)
    setRows(body.rows)
    setChecked(body.rows.map((r) => r.valid && !r.duplicate_in_file))
  }

  async function handleImport() {
    if (!rows) return
    const selection = rows.filter((_, i) => checked[i]).map(({ first_name, last_name, email_address }) => ({ first_name, last_name, email_address }))
    if (selection.length === 0) return
    setBusy(true)
    const response = await fetch(`${basePath}/import`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
      body: JSON.stringify({ rows: selection }),
    })
    setBusy(false)
    if (!response.ok) {
      setFileErrors(['Import failed. Please try again.'])
      return
    }
    setResult(await response.json())
  }

  // Runs on every close path (button, Esc, backdrop) so a picked file or
  // preview never lingers into the next open.
  function handleClosed() {
    const imported = !!result
    reset()
    if (imported) router.reload({ only: ['respondents', 'counts'] })
  }

  const selectedCount = checked.filter(Boolean).length

  return (
    <dialog ref={dialogRef} className="modal" onClose={handleClosed}>
      <div className="modal-box max-w-2xl">
        <h3 className="text-lg font-bold">Import respondents</h3>

        {result ? (
          <div className="py-4 space-y-3">
            <div role="alert" className="alert alert-soft alert-success text-sm">
              <span className="iconify lucide--check size-4" />
              <span>{result.created} respondent{result.created === 1 ? '' : 's'} added.</span>
            </div>
            {result.failed.length > 0 && (
              <div role="alert" className="alert alert-soft alert-warning text-sm">
                <span className="iconify lucide--alert-triangle size-4" />
                <div>
                  <p>{result.failed.length} row{result.failed.length === 1 ? '' : 's'} could not be added:</p>
                  <ul className="list-disc list-inside">
                    {result.failed.map((f, i) => (
                      <li key={i}>{f.email_address}: {f.errors.join(', ')}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
          </div>
        ) : rows ? (
          <div className="py-4 space-y-3">
            {fileErrors.map((err, i) => (
              <div key={i} role="alert" className="alert alert-soft alert-error text-sm"><span>{err}</span></div>
            ))}
            <p className="text-sm text-base-content/60">
              {selectedCount} of {rows.length} rows selected. Rows with problems are unticked.
            </p>
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="table table-sm">
                <thead>
                  <tr><th></th><th>Name</th><th>Email</th><th>Notes</th></tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className={!row.valid ? 'opacity-60' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          className="checkbox checkbox-sm"
                          checked={checked[i] ?? false}
                          disabled={!row.valid}
                          onChange={() => setChecked((c) => c.map((v, j) => (j === i ? !v : v)))}
                        />
                      </td>
                      <td>{`${row.first_name} ${row.last_name}`.trim()}</td>
                      <td>{row.email_address}</td>
                      <td className="text-xs">
                        {row.errors.map((e, j) => <span key={j} className="text-error">{e} </span>)}
                        {row.duplicate_in_file && <span className="text-warning">Duplicate row in file. </span>}
                        {row.already_respondent && <span className="text-base-content/50">Already on this survey.</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="py-4 space-y-4">
            {fileErrors.map((err, i) => (
              <div key={i} role="alert" className="alert alert-soft alert-error text-sm"><span>{err}</span></div>
            ))}

            <div className="rounded-lg border border-base-300 p-3">
              <p className="text-sm font-medium">Step 1 — Download the template</p>
              <p className="text-xs text-base-content/60 mt-0.5 mb-2">
                Fill in one respondent per row (First Name, Last Name, Email), then upload it below.
              </p>
              {/* Plain anchor so the browser downloads the file rather than an Inertia visit. */}
              <a href={`${basePath}/import_template`} className="btn btn-outline btn-sm" download>
                <span className="iconify lucide--download size-4" />
                Download Excel template
              </a>
            </div>

            <div>
              <p className="text-sm font-medium mb-2">Step 2 — Upload your file</p>
              <input
                ref={fileInput}
                type="file"
                accept=".xlsx,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                className="hidden"
                onChange={(e) => acceptFile(e.target.files?.[0])}
              />
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => { e.preventDefault(); setDragging(false); acceptFile(e.dataTransfer.files?.[0]) }}
                className={`w-full flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                  dragging ? 'border-primary bg-primary/5' : 'border-base-300 hover:border-base-content/30'
                }`}
              >
                {file ? (
                  <>
                    <span className="iconify lucide--file-spreadsheet size-6 text-primary" />
                    <span className="text-sm font-medium">{file.name}</span>
                    <span className="text-xs text-base-content/50">Click to choose a different file</span>
                  </>
                ) : (
                  <>
                    <span className="iconify lucide--upload-cloud size-6 text-base-content/40" />
                    <span className="text-sm"><span className="text-primary font-medium">Click to browse</span> or drag a file here</span>
                    <span className="text-xs text-base-content/50">Excel (.xlsx) or CSV, up to 500 rows</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        <div className="modal-action">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => dialogRef.current?.close()}>
            {result ? 'Done' : 'Cancel'}
          </button>
          {!result && (rows ? (
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || selectedCount === 0} onClick={() => void handleImport()}>
              {busy && <span className="loading loading-spinner loading-sm" />}
              Import {selectedCount} row{selectedCount === 1 ? '' : 's'}
            </button>
          ) : (
            <button type="button" className="btn btn-primary btn-sm" disabled={busy || !file} onClick={() => void handlePreview()}>
              {busy && <span className="loading loading-spinner loading-sm" />}
              Preview
            </button>
          ))}
        </div>
      </div>
      <form method="dialog" className="modal-backdrop"><button>close</button></form>
    </dialog>
  )
}

/* ----- Compose and send (invitations / reminders) ----- */

interface SendModalProps {
  dialogRef: React.RefObject<HTMLDialogElement | null>
  kind: 'invitation' | 'reminder'
  basePath: string
  audience: RespondentInviteRow[]
  surveyActive: boolean
  initialSubject: string
  initialBody: string
}

function SendModal({ dialogRef, kind, basePath, audience, surveyActive, initialSubject, initialBody }: SendModalProps) {
  const subjectInput = useRef<HTMLInputElement>(null)
  const [subject, setSubject] = useState(initialSubject)
  const [body, setBody] = useState(initialBody)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [queued, setQueued] = useState<number | null>(null)
  // Remount the editor (and reset subject) when the kind or template changes.
  const [editorKey, setEditorKey] = useState('')
  const currentKey = `${kind}:${initialSubject}`
  if (editorKey !== currentKey) {
    setEditorKey(currentKey)
    setSubject(initialSubject)
    setBody(initialBody)
    setQueued(null)
    setError(null)
  }

  const alreadyInvitedCount = audience.filter((r) => r.invitation_sent_at).length

  async function handleSend() {
    setBusy(true)
    setError(null)
    const payload: Record<string, unknown> = {
      survey_respondent_ids: audience.map((r) => r.id),
      [`${kind}_subject`]: subject,
      [`${kind}_body`]: body,
    }
    const response = await fetch(`${basePath}/send_${kind === 'invitation' ? 'invitations' : 'reminders'}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken(), Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
    setBusy(false)
    if (!response.ok) {
      const bodyJson = await response.json().catch(() => ({}))
      setError((bodyJson as { error?: string }).error ?? 'Sending failed. Please try again.')
      return
    }
    const { queued: n } = (await response.json()) as { queued: number }
    setQueued(n)
  }

  function close() {
    dialogRef.current?.close()
    const sent = queued !== null
    setQueued(null)
    setError(null)
    if (sent) router.reload({ only: ['respondents', 'counts'] })
  }

  const title = kind === 'invitation' ? 'Send invitations' : 'Send reminders'

  return (
    <dialog ref={dialogRef} className="modal">
      <div className="modal-box max-w-2xl">
        <h3 className="text-lg font-bold">{title}</h3>

        {queued !== null ? (
          <div className="py-4">
            <div role="alert" className="alert alert-soft alert-success text-sm">
              <span className="iconify lucide--check size-4" />
              <span>{queued} email{queued === 1 ? '' : 's'} queued for delivery.</span>
            </div>
          </div>
        ) : (
          <div className="py-4 space-y-3">
            {!surveyActive && (
              <div role="alert" className="alert alert-soft alert-warning text-sm">
                <span className="iconify lucide--alert-triangle size-4" />
                <span>The survey isn't active — publish it before sending, or these links will be dead on arrival.</span>
              </div>
            )}
            {error && (
              <div role="alert" className="alert alert-soft alert-error text-sm"><span>{error}</span></div>
            )}
            <p className="text-sm text-base-content/60">
              Going to <strong>{audience.length}</strong> respondent{audience.length === 1 ? '' : 's'}
              {kind === 'reminder' ? ' who have been invited but not yet completed' : ' who haven\'t completed yet'}.
              Each email includes their personal survey link.
            </p>

            {kind === 'invitation' && alreadyInvitedCount > 0 && (
              <div role="alert" className="alert alert-soft alert-warning text-sm">
                <span className="iconify lucide--alert-triangle size-4" />
                <span>
                  {alreadyInvitedCount} of these {alreadyInvitedCount === 1 ? 'has' : 'have'} already been invited and will
                  get the invitation again.
                </span>
              </div>
            )}

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Subject</legend>
              <div className="flex items-center gap-2">
                <input ref={subjectInput} type="text" className="input input-sm w-full" value={subject} onChange={(e) => setSubject(e.target.value)} />
                <VariableMenu onInsert={(token) => {
                  const input = subjectInput.current
                  const start = input?.selectionStart ?? subject.length
                  const end = input?.selectionEnd ?? start
                  setSubject(subject.slice(0, start) + token + subject.slice(end))
                }} />
              </div>
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Message</legend>
              <RichTextEditor key={editorKey} content={initialBody} onChange={setBody} minHeight="min-h-40" withVariables />
              <p className="text-xs text-base-content/60 mt-1">
                The survey link button is always added below your message. Edits here are saved as this survey's template.
              </p>
            </fieldset>
          </div>
        )}

        <div className="modal-action">
          <button type="button" className="btn btn-ghost btn-sm" onClick={close}>
            {queued !== null ? 'Done' : 'Cancel'}
          </button>
          {queued === null && (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || audience.length === 0 || !surveyActive || !subject.trim()}
              onClick={() => void handleSend()}
            >
              {busy && <span className="loading loading-spinner loading-sm" />}
              Send to {audience.length}
            </button>
          )}
        </div>
      </div>
      <form method="dialog" className="modal-backdrop"><button onClick={close}>close</button></form>
    </dialog>
  )
}
